// TSK-11215, ASMT-CAND-18: the server-side half of "only one screen can save/submit per (account, assessment)".
//
// The taking screen claims a seat on every mount (PUT /v2/assessments/{id}/status?status=START).
// Writes carry it back as `X-Seat-Id`; the 15-30s session-state poll reports `seatValid:false`
// once a newer screen has taken it over. That is the CROSS-DEVICE signal — same-browser tabs
// find out instantly over BroadcastChannel (useSessionOwner) instead. Both drive one popup.
//
// Module-level refs on purpose: the claim happens on the page, the header read happens in the
// API layer, and the loss is detected in the poll — three places that need one shared value.
//
// ponytail: module-level refs are a Nitro-process singleton, NOT request-scoped like
// useState(). INVARIANT: only ever write these from client-side lifecycle hooks
// (onMounted/onBeforeUnmount) or from code reached by them. An SSR-time write would leak one
// user's seat to every concurrent request in the same worker. useState() is not an option
// here because seatHeader() is called from the API layer (package/apis/*, package/network/*),
// which has no Nuxt setup context. Upgrade path if this ever needs SSR writes: move the state
// into a Pinia store and pass it into the API layer explicitly.

import { ref, readonly, type Ref } from 'vue';

const seatId = ref<string | null>(null);
const seatLost = ref(false);
let claiming: Promise<string | null> | null = null;

export const useAssessmentSeat = (): {
    seatId: Readonly<Ref<string | null>>;
    seatLost: Readonly<Ref<boolean>>;
    claimSeat: (claim: () => Promise<string>) => Promise<string | null>;
    release: (claimed: string | null) => void;
    setSeat: (id: string | null) => void;
    markLost: () => void;
    reset: () => void;
} => ({
    seatId: readonly(seatId),
    seatLost: readonly(seatLost),

    /**
     * Claim a seat unless this screen already holds one. Resolves with the seat now held.
     *
     * The taking screen mounts TWICE — its sets/remaining-time fetches were already
     * duplicated before seats existed, which was harmless because those are reads. Every
     * START mints a FRESH seat, so two claims race: the server keeps whichever committed
     * last, this screen keeps whichever response landed last, and those need not agree —
     * locking the candidate out of their own exam on their only screen. Deduping on the
     * in-flight promise makes the second mount a no-op no matter how the two overlap.
     */
    claimSeat: (claim: () => Promise<string>): Promise<string | null> => {
        if (seatId.value) return Promise.resolve(seatId.value);
        if (!claiming) {
            claiming = claim()
                .then((id) => {
                    seatId.value = id;
                    seatLost.value = false;
                    return id;
                })
                .catch(() => null) // best-effort; reads still work, writes will 409
                .finally(() => { claiming = null; });
        }
        return claiming;
    },

    /**
     * Hand back the seat THIS screen claimed, and only that one.
     *
     * reset() cannot be used for unmount. The two instances' hooks interleave, and Vue can
     * mount the second before unmounting the first — so a blind reset from the departing
     * instance wipes the seat the surviving one is holding. That is observable: the poll
     * then goes out with no X-Seat-Id, the server reports seatValid:false, and the
     * candidate gets "another screen took over" while sitting at their only screen.
     */
    release: (claimed: string | null): void => {
        if (claimed !== null && seatId.value !== claimed) return;
        seatId.value = null;
        seatLost.value = false;
        claiming = null;
    },

    /** Store the seat handed out by the entry call. Clears any prior "lost" state. */
    setSeat: (id: string | null): void => {
        seatId.value = id;
        seatLost.value = false;
    },

    /** A newer screen took the seat: this screen may no longer save or submit. */
    markLost: (): void => {
        seatLost.value = true;
    },

    /**
     * Leaving the taking screen. Drops any in-flight claim too — seats are per-assessment,
     * so a claim still running for the screen we just left must never be handed to the next.
     */
    reset: (): void => {
        seatId.value = null;
        seatLost.value = false;
        claiming = null;
    },
});

/** Header for write calls. Empty when no seat is held, so nothing bogus is sent. */
export const seatHeader = (): Record<string, string> =>
    seatId.value ? { 'X-Seat-Id': seatId.value } : {};
